import { Component, OnInit, OnChanges, SimpleChanges, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { RolesApiService } from '@angular-workspace/services';
import { WorkspaceContextService } from '../../../services/workspace-context.service';

@Component({
  selector: 'app-page-header',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './page-header.component.html',
  styleUrl: './page-header.component.scss',
})
export class PageHeaderComponent implements OnInit, OnChanges {
  title: string = '';
  description: string = '';

  @Input() variant: 'default' | 'compact-inline' = 'compact-inline';

  /** When false, the teal `<hr>` is omitted so the host template can render a full-bleed divider. */
  @Input() showDivider = true;

  /** Optional overrides — when set, take precedence over route `data.title` / `data.description`. */
  @Input() titleOverride?: string | null;
  @Input() descriptionOverride?: string | null;

  private rolesService = inject(RolesApiService);
  private workspaceContext = inject(WorkspaceContextService);

  constructor(
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    setTimeout(() => {
      this.updateHeaderData();
    }, 0);

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        setTimeout(() => {
          this.updateHeaderData();
        }, 0);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['titleOverride'] || changes['descriptionOverride']) {
      this.updateHeaderData();
    }
  }

  private updateHeaderData(): void {
    let currentRoute = this.route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    let title = currentRoute.snapshot.data['title'] || '';
    let description = currentRoute.snapshot.data['description'] || '';

    if (!title || !description) {
      let route: ActivatedRoute | null = currentRoute;
      while (route) {
        const data = route.snapshot.data;
        if (data && data['title'] && !title) {
          title = data['title'];
        }
        if (data && data['description'] && !description) {
          description = data['description'];
        }
        route = route.parent;
      }
    }

    if (!title || !description) {
      const rootData = this.route.root.snapshot.data;
      if (rootData && rootData['title'] && !title) {
        title = rootData['title'];
      }
      if (rootData && rootData['description'] && !description) {
        description = rootData['description'];
      }
    }

    this.title = this.titleOverride || title;

    if (this.descriptionOverride !== undefined && this.descriptionOverride !== null) {
      this.description = this.descriptionOverride;
    } else if (description === 'Welcome !!') {
      this.updateDescriptionWithRole();
    } else {
      this.description = description;
    }
  }

  private async updateDescriptionWithRole(): Promise<void> {
    try {
      if (this.workspaceContext.isDelegateSession()) {
        const label = this.workspaceContext.getActiveDelegatedWorkspace()?.roleLabel;
        if (label) {
          this.description = `Welcome to ${label} Panel !!`;
          return;
        }
      }

      await this.rolesService.getRoles();
      const userRoles = this.rolesService.getUserRoles();

      if (userRoles && userRoles.length > 0) {
        const highestRole = this.rolesService.getHighestRole(userRoles);

        if (highestRole) {
          const formattedRole =
            highestRole.charAt(0).toUpperCase() + highestRole.slice(1).toLowerCase();
          this.description = `Welcome to ${formattedRole} Panel !!`;
        } else {
          this.description = 'Welcome !!';
        }
      } else {
        this.description = 'Welcome !!';
      }
    } catch (error) {
      console.error('Error getting highest role for welcome message:', error);
      this.description = 'Welcome !!';
    }
  }
}
